import { SolButtonType, SolText } from '@sol/components';
import { ObjectTools } from '@sol/components/tools/object-tools';
import { SolIcon } from '@sol/icons';
import { LabAction } from '../../tools/lab-action';
import { SolErrorPageType } from './lab-error-page.component';

export class State {
  private constructor(
    public readonly type: SolErrorPageType,
    public readonly callbackUri: string | undefined,
    public readonly username: string | undefined,
    public readonly authenticationEnabled: boolean,
    public readonly heading: SolText,
    public readonly action: LabAction | undefined,
    public readonly messages: SolText[],
    public readonly showUsername: boolean,
    public readonly hostClass: string,
  ) {}

  public static of(): State {
    return new State(
      SolErrorPageType.UNKNOWN,
      undefined,
      undefined,
      false,
      State.toTitle(SolErrorPageType.UNKNOWN),
      State.toAction(SolErrorPageType.UNKNOWN, false),
      State.toMessages(SolErrorPageType.UNKNOWN),
      false,
      'lab-error-page',
    );
  }

  public withResolved(type: SolErrorPageType, callbackUri: string | undefined, authenticationEnabled: boolean): State {
    if (
      type === this.type &&
      callbackUri === this.callbackUri &&
      authenticationEnabled === this.authenticationEnabled
    ) {
      return this;
    }
    return this.copy({
      type,
      callbackUri,
      authenticationEnabled,
      heading: State.toTitle(type),
      action: State.toAction(type, authenticationEnabled),
      messages: State.toMessages(type),
      showUsername: type === SolErrorPageType.FORBIDDEN,
    });
  }

  public withUsername(username: string | undefined): State {
    if (username === this.username) {
      return this;
    }
    return this.copy({ username });
  }

  private copy(props: Partial<State>): State {
    return ObjectTools.copy<State>(this, props);
  }

  private static toMessages(type: SolErrorPageType): SolText[] {
    switch (type) {
      case SolErrorPageType.NOT_FOUND:
        return [SolText.of('lab-error-page.not-found.message1'), SolText.of('lab-error-page.not-found.message2')];
      case SolErrorPageType.CONNECTION_ERROR:
        return [
          SolText.of('lab-error-page.connection-error.message1'),
          SolText.of('lab-error-page.connection-error.message2'),
        ];
      case SolErrorPageType.FORBIDDEN:
        return [SolText.of('lab-error-page.forbidden.message1'), SolText.of('lab-error-page.forbidden.message2')];
      default:
        return [SolText.of('lab-error-page.unknown.message1')];
    }
  }

  private static toTitle(type: SolErrorPageType): SolText {
    switch (type) {
      case SolErrorPageType.NOT_FOUND:
        return SolText.of('lab-error-page.not-found.title');
      case SolErrorPageType.CONNECTION_ERROR:
        return SolText.of('lab-error-page.connection-error.title');
      case SolErrorPageType.FORBIDDEN:
        return SolText.of('lab-error-page.forbidden.title');
      default:
        return SolText.of('lab-error-page.unknown.title');
    }
  }

  private static toAction(type: SolErrorPageType, authenticationEnabled: boolean): LabAction | undefined {
    switch (type) {
      case SolErrorPageType.NOT_FOUND:
        return {
          text: SolText.of('lab-error-page.not-found.button-text'),
          variant: SolButtonType.transparent,
          icon: SolIcon.back,
        };
      case SolErrorPageType.CONNECTION_ERROR:
        return { text: SolText.of('lab-error-page.connection-error.button-text'), variant: SolButtonType.link };
      case SolErrorPageType.FORBIDDEN:
        return authenticationEnabled
          ? { text: SolText.of('lab-error-page.forbidden.button-text'), variant: SolButtonType.link }
          : undefined;
      default:
        return undefined;
    }
  }
}
